import React, { forwardRef } from "react"
import type { ReactNode } from "react"
import { StandaloneSelect, ComposableSelect } from "./select"
import type { SelectOption } from "./select"
import type { StandaloneSelectProps } from "./select"
import { AutoComplete, ComposableAutocomplete } from "./autocomplete"
import type {
  FilterFn,
  AsyncOptionsFn,
  AutoCompleteProps,
  ComposableAutocompleteProps
} from "./autocomplete"
import Option from "./option"

export type { SelectOption, FilterFn, AsyncOptionsFn }
export type { OptionProps } from "./option"
export type { ComposableSelectProps } from "./select"
export type { ComposableAutocompleteProps } from "./autocomplete"

/** Branded type so only the exported `separator` constant satisfies this, not arbitrary JSX elements. */
export type SelectSeparator = JSX.Element & { __brand?: "separator" }

export type SelectChangeEvent = {
  target: {
    label: ReactNode
    value: string | number
    name?: string
  }
}

type SelectMenuPublicProps = {
  /** Visual type of the select — "autocomplete" adds a text input for filtering */
  type?: "select" | "autocomplete"
  /** Additional CSS classes */
  className?: string
  /** When true, removes border and uses tertiary button styling */
  borderless?: boolean
  /** Visible label displayed above the select */
  label?: string
  /** Accessible label for screen readers */
  "aria-label"?: string
  /** ID of an external element that labels this select */
  "aria-labelledby"?: string
  /** Name attribute, included in the onChange event target */
  name?: string
  /** Whether the select is in an error state */
  error?: boolean
  /** Called when a new option is selected */
  onChange?: (event: SelectChangeEvent) => void
  /** Options array, or an async function that returns options when async is true */
  options: (SelectOption | ReactNode)[] | AsyncOptionsFn
  /** Currently selected value, or a prefilled {label, value} for async autocomplete */
  value?: string | number | { label: string; value: string }
  /** Text shown when no option is selected */
  placeholder?: string
  /** When true, changes the type to "autocomplete" and enables async option loading */
  async?: boolean
  /** Allows adding new options. When a string, overrides the default "Create" prefix */
  create?: boolean | string
  /** Whether the select is disabled */
  disabled?: boolean
  /** Whether to hide the dropdown caret icon */
  hideCaretIcon?: boolean
  /** Minimum characters before triggering async load */
  minCharsToLoad?: number
  /** Custom filter function for autocomplete options */
  filter?: FilterFn
}

export type SelectMenuProps = SelectMenuPublicProps &
  Omit<React.HTMLAttributes<HTMLElement>, keyof SelectMenuPublicProps>

/**
 * A dropdown select component that supports option arrays, autocomplete filtering, async loading, and composable children.
 * Use `SelectMenu` with an `options` array for the simplest case, or `Select` + `Option` for full control over option rendering.
 */
const SelectMenu = forwardRef<HTMLElement, SelectMenuProps>(
  ({ type = "select", ...props }, ref) => {
    if (props.async || props.create || type === "autocomplete") {
      return (
        <AutoComplete
          {...(props as AutoCompleteProps)}
          ref={ref as React.Ref<HTMLInputElement>}
        />
      )
    }
    return (
      <StandaloneSelect
        {...(props as StandaloneSelectProps)}
        ref={ref as React.Ref<HTMLDivElement>}
      />
    )
  }
)

export { SelectMenu }
export default SelectMenu

type SelectProps = Omit<
  SelectMenuProps,
  "options" | "value" | "async" | "minCharsToLoad"
> & { children?: ReactNode }

const Select = forwardRef<HTMLElement, SelectProps>(
  ({ type = "select", ...props }, ref) => {
    if ((props as Record<string, unknown>).async) {
      throw new Error(
        "Select component does not support async prop. Use SelectMenu instead."
      )
    }
    if (type === "autocomplete") {
      return (
        <ComposableAutocomplete
          {...(props as ComposableAutocompleteProps)}
          ref={ref as React.Ref<HTMLInputElement>}
        />
      )
    }
    return (
      <ComposableSelect {...props} ref={ref as React.Ref<HTMLDivElement>} />
    )
  }
)

export { Select, Option }

function Separator() {
  return <div className="border-b border-gray-200 my-1" role="separator"></div>
}
export const separator = Separator()
